import { type ReactNode, useId } from 'react';
import clsx from 'clsx';
import { Modal, IconButton, Icon, Badge, useBreakpoint } from '@nula/core-ui';
import styles from './settings-shell.module.css';

export interface SettingsTab {
  /** Stable tab id used for active-tab tracking and onTabChange callback. */
  id: string;
  /** Display label rendered in the nav. */
  label: string;
  /** Material Symbols icon name (e.g., `'person'`, `'palette'`). Matches the
   * existing `Tab` interface in `SettingsModal.tsx`. `<SettingsShell>`
   * renders `<Icon name={icon} size={16} />` internally. */
  icon: string;
  /** When true, the tab is filtered out of the rendered nav. */
  hidden?: boolean;
  /** Renders an attention dot beside the label. Used by Updates so a
   * dismissed update notification leaves a trace the user can find. */
  badge?: boolean;
}

export interface SettingsShellProps {
  /** Modal open state. Forwarded to core-ui `<Modal>` which owns the portal,
   * overlay, focus trap, ESC-to-close, and animation. */
  open: boolean;
  title: string;
  tabs: SettingsTab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  onClose: () => void;
  /** Tab content. */
  children: ReactNode;
  /** When set, renders a divider + section label before tabs whose id starts
   * with `ws-`. Used by SettingsModal to separate user tabs from workspace tabs. */
  workspaceSectionLabel?: string;
  /** Optional content slot at the bottom of the desktop sidebar — for
   * "content not chrome" items like the version tag.
   * Hidden in the mobile horizontal-strip layout. */
  footer?: ReactNode;
  className?: string;
}

export function SettingsShell({
  open,
  title,
  tabs,
  activeTabId,
  onTabChange,
  onClose,
  children,
  workspaceSectionLabel,
  footer,
  className,
}: SettingsShellProps) {
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const titleId = useId();
  const bp = useBreakpoint();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      footer={null}
      closable={false}
      centered
      width={bp.md ? 800 : '100vw'}
      style={bp.md ? { height: 640 } : undefined}
      bodyStyle={{ padding: 0 }}
      aria-labelledby={titleId}
    >
      <div className={clsx(styles.shell, className)}>
        <div className={styles.body}>
          <nav className={styles.nav} aria-label="Settings navigation">
            <div id={titleId} className={styles.navTitle}>{title}</div>
            {visibleTabs.map((tab, i) => {
              const isFirstWsTab = workspaceSectionLabel && tab.id.startsWith('ws-') && !visibleTabs[i - 1]?.id.startsWith('ws-');
              return (
                <span key={tab.id}>
                  {isFirstWsTab && (
                    <div className={styles.navSectionDivider}>
                      <span className={styles.navSectionLabel}>{workspaceSectionLabel}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.navItem}
                    data-active={tab.id === activeTabId}
                    data-badge={tab.badge ? 'true' : undefined}
                    aria-current={tab.id === activeTabId ? 'page' : undefined}
                    onClick={() => onTabChange(tab.id)}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span>{tab.label}</span>
                    {tab.badge && <Badge dot status="processing" className={styles.navBadge} />}
                  </button>
                </span>
              );
            })}
            {footer && <div className={styles.navFooter}>{footer}</div>}
          </nav>
          <div className={styles.content}>
            {/*
              IconButton is wrapped by Pressable, which clones the button and
              forces `position: relative` on it (the ripple node needs a
              non-static positioning context). That clobbers any
              `position: absolute` we'd put on the button via className, so
              the close button positions itself with a sibling wrapper div
              instead of styling the button directly.
            */}
            <div className={styles.closeBtnSlot}>
              <IconButton size="sm" aria-label="Close settings" onClick={onClose}>
                <Icon name="close" size={16} />
              </IconButton>
            </div>
            <div className={styles.contentHeader}>
              <div className={styles.contentTitle}>
                {visibleTabs.find((t) => t.id === activeTabId)?.label ?? title}
              </div>
            </div>
            <div className={styles.contentBody}>{children}</div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
